import { createFileRoute, Link } from "@tanstack/react-router";
import { StaticPage, staticHead } from "@/components/StaticPage";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () =>
    staticHead({
      title: "About AutoSpecData — Vehicle Specs From Official Sources",
      description:
        "AutoSpecData publishes MPG, engine, transmission and maintenance data for thousands of vehicles, sourced from the EPA and NHTSA.",
      path: "/about",
    }),
});

function AboutPage() {
  return (
    <StaticPage
      title="About AutoSpecData"
      intro="AutoSpecData is a free vehicle-data reference. Every number on the site comes from a named public source and is shown with the year and trim it belongs to."
    >
      <section>
        <h2>Why this site exists</h2>
        <p>
          Vehicle specifications are public information, but they are scattered across
          government databases, PDF owner's manuals, and forum posts. Most sites that
          aggregate them strip out the year, the trim, and the source, which is exactly the
          context that decides whether a number applies to your car. AutoSpecData keeps that
          context attached to every value.
        </p>
      </section>
      <section>
        <h2>What you'll find</h2>
        <ul>
          <li>EPA fuel economy: city, highway, and combined MPG by year and trim</li>
          <li>Engine configuration: cylinders, displacement, and fuel type</li>
          <li>Transmission and drivetrain by model year</li>
          <li>EPA vehicle class and estimated annual fuel cost</li>
          <li>Maintenance intervals computed from each vehicle's powertrain</li>
          <li>Side-by-side model comparisons and fuel-economy rankings</li>
        </ul>
      </section>
      <section>
        <h2>How we work</h2>
        <p>
          Our{" "}
          <Link to="/methodology">methodology page</Link> explains where each data type
          comes from, how it is refreshed, and what its limits are. If a value is not
          verified, we do not publish a page for it.
        </p>
      </section>
      <section>
        <h2>Publisher & editorial standards</h2>
        <p>
          AutoSpecData is independently published and edited. Content is produced by
          researchers and developers who specialize in automotive data, then cross-checked
          against the original EPA and NHTSA records before a page goes live. We do not accept
          payment from manufacturers to alter specifications, and we do not publish
          user-generated estimates without verification.
        </p>
      </section>
      <section>
        <h2>How we make money</h2>
        <p>
          AutoSpecData is free to use. We support the site through two channels:
        </p>
        <ul>
          <li>
            <strong>Advertising.</strong> We display ads served by Google AdSense. Google and
            its partners may use cookies to serve ads based on your visits to this and other
            sites. You can manage personalized ads in{" "}
            <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
              Google Ads Settings
            </a>
            .
          </li>
        </ul>
      </section>
      <section>
        <h2>Content quality</h2>
        <p>
          Every vehicle page is built from structured, verifiable records rather than scraped
          or rewritten content. When a value is missing, we say so instead of guessing. Pages
          are reviewed for accuracy, updated when source data changes, and removed if they no
          longer meet our minimum-data threshold.
        </p>
      </section>
      <section>
        <h2>Corrections & press</h2>
        <p>
          If you spot a value that does not match your vehicle's documentation, tell us on
          the <Link to="/contact">contact page</Link> or email{" "}
          <a
            href="mailto:contact@autospecdata.com"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            contact@autospecdata.com
          </a>
          . For press, advertising, and bulk-data enquiries, email{" "}
          <a
            href="mailto:info@autospecdata.com"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            info@autospecdata.com
          </a>
          . Include the year, trim, and the source you are comparing against.
        </p>
      </section>
    </StaticPage>
  );
}
